import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { useCatalog } from '../context/CatalogContext';
import { useSearch } from '../context/SearchContext';
import { useCart } from '../context/CartContext';
import {
  fixImageUrl, rootsInSection, childrenInSection, collectDescendantIds,
  normalizeCategoryId, buildBannerList,
} from '../lib/format';
import ProductCard from '../components/ProductCard';
import Button from '../lib/ui/Button';
import { SkeletonCard, EmptyState } from '../lib/ui/Feedback';

const API = (process.env.REACT_APP_API_URL || 'https://nrkmart-api.vercel.app').replace(/\/$/, '');

const STORE_TONES = {
  groceries: { bg: 'bg-brand' },
  food: { bg: 'bg-sun' },
  bazaar: { bg: 'bg-berry' },
  default: { bg: 'bg-brand' },
};

export default function HomePage() {
  const { lang, t } = useLang();
  const te = lang === 'te';
  const navigate = useNavigate();
  const location = useLocation();
  const { setToastMsg } = useOutletContext();
  const {
    products, categories, loading, loadError, retryLoad,
    activeSection, selectStore, storeTabs, deliverTo, refreshDeliverTo,
  } = useCatalog();
  const { search, setSearch } = useSearch();
  const { itemCount, total } = useCart();
  const [placedResult, setPlacedResult] = useState(location.state?.placedResult || null);

  const [l1Id, setL1Id] = useState('all');
  const [l2Id, setL2Id] = useState('all');
  const [l3Id, setL3Id] = useState('all');
  const [banners, setBanners] = useState([]);
  const [heroIndex, setHeroIndex] = useState(0);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [offerFilterCat, setOfferFilterCat] = useState(null);
  const catsRef = useRef(null);

  useEffect(() => {
    const onAdded = (e) => {
      const name = e?.detail?.name || (te ? 'ఉత్పత్తి' : 'Item');
      setToastMsg(te ? `${name} కార్ట్‌లో జోడించబడింది` : `${name} added to cart`);
      setTimeout(() => setToastMsg(''), 1600);
    };
    window.addEventListener('nrk:item-added', onAdded);
    return () => window.removeEventListener('nrk:item-added', onAdded);
  }, [te, setToastMsg]);

  useEffect(() => {
    if (!API || !activeSection) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const q = `sectionId=${encodeURIComponent(activeSection)}`;
        const [offersRes, promosRes] = await Promise.all([
          axios.get(`${API}/api/offers?${q}`).catch(() => ({ data: { offers: [] } })),
          axios.get(`${API}/api/promos/active?${q}`).catch(() => ({ data: { promos: [] } })),
        ]);
        if (cancelled) return;
        setBanners(buildBannerList(offersRes.data?.offers, promosRes.data?.promos));
      } catch {
        if (!cancelled) setBanners([]);
      }
    })();
    return () => { cancelled = true; };
  }, [activeSection]);

  useEffect(() => { setHeroIndex(0); }, [banners.length, activeSection]);
  useEffect(() => {
    if (banners.length <= 1) return undefined;
    const timer = setInterval(() => setHeroIndex((p) => (p + 1) % banners.length), 4200);
    return () => clearInterval(timer);
  }, [banners.length]);

  const selectStoreAndReset = (id) => {
    selectStore(id);
    setL1Id('all'); setL2Id('all'); setL3Id('all'); setOfferFilterCat(null);
  };

  const handleBannerClick = (b) => {
    const link = String(b?.link_url || b?.href || '').trim();
    if (link) { try { window.open(link, '_blank', 'noopener,noreferrer'); } catch { window.location.href = link; } return; }
    if (b?.category_id) {
      setOfferFilterCat(b.category_id);
      setL1Id('all'); setL2Id('all'); setL3Id('all'); setSearch('');
      try { document.getElementById('categories-section')?.scrollIntoView({ behavior: 'smooth' }); } catch { /* ignore */ }
      return;
    }
    setBannerPreview(b);
  };

  const l1Cats = useMemo(() => rootsInSection(categories, activeSection), [categories, activeSection]);
  const l2Cats = useMemo(() => (l1Id === 'all' ? [] : childrenInSection(categories, l1Id, activeSection)), [categories, l1Id, activeSection]);
  const l3Cats = useMemo(() => (l2Id === 'all' ? [] : childrenInSection(categories, l2Id, activeSection)), [categories, l2Id, activeSection]);
  const activeCatFilter = l3Id !== 'all' ? l3Id : l2Id !== 'all' ? l2Id : l1Id !== 'all' ? l1Id : null;

  const filtered = products.filter((p) => {
    const productCategoryId = normalizeCategoryId(p.category);
    const cat = categories.find((c) => c.id === productCategoryId);
    if (!cat || cat.section_id !== activeSection) return false;
    if (offerFilterCat) {
      const allowed = collectDescendantIds(offerFilterCat, categories);
      if (!allowed.has(productCategoryId)) return false;
    } else if (activeCatFilter) {
      const allowed = collectDescendantIds(activeCatFilter, categories);
      if (!allowed.has(productCategoryId)) return false;
    }
    const matchSearch = !search || p.name_en.toLowerCase().includes(search.toLowerCase()) || (p.name_te || '').includes(search);
    return matchSearch;
  }).sort((a, b) => (a.display_order || 99) - (b.display_order || 99));

  const activeStore = storeTabs.find((s) => s.id === activeSection) || storeTabs[0];
  const storeLabel = te ? (activeStore?.name_te || activeStore?.name_en || 'Groceries') : (activeStore?.name_en || 'Groceries');
  const deliverLabel = deliverTo?.label || (te ? 'డెలివరీ స్థానం సెట్ చేయండి' : 'Set delivery location');
  const openProduct = (p) => navigate(`/product/${p.id}`, { state: { backgroundLocation: location } });

  const unpaidPlaced = !!(placedResult && ['razorpay', 'cashfree', 'upi'].includes(placedResult.payment_method) && placedResult.payment_status !== 'verified');
  const closePlaced = () => setPlacedResult(null);
  const viewOrders = () => { setPlacedResult(null); navigate('/orders'); };

  return (
    <div className="max-w-6xl mx-auto px-3 md:px-6">
      {/* mobile deliver chip (own row) */}
      <button
        type="button"
        onClick={refreshDeliverTo}
        className="md:hidden mt-3 flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-black/5 text-xs font-bold text-ink-soft w-full"
      >
        📍<span className="truncate">{deliverLabel}</span>
      </button>

      {/* store tabs */}
      <div className="flex items-center gap-2 pt-3 pb-1 md:pt-5">
        <div role="tablist" aria-label="Stores" className="flex gap-2 flex-1 overflow-x-auto no-scrollbar">
          {storeTabs.map((sec) => {
            const label = te ? (sec.name_te || sec.name_en) : sec.name_en;
            const active = activeSection === sec.id;
            const tone = STORE_TONES[sec.id] || STORE_TONES.default;
            return (
              <button
                key={sec.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => selectStoreAndReset(sec.id)}
                className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-extrabold transition-colors ${
                  active ? `${tone.bg} text-white shadow-card` : 'bg-white text-ink-soft border border-black/5'
                }`}
              >
                <span>{sec.emoji || '🏪'}</span>{label}
              </button>
            );
          })}
        </div>
      </div>

      {/* hero banner */}
      {banners.length > 0 ? (
        <div className="mt-3 relative rounded-2xl md:rounded-3xl overflow-hidden shadow-lift ring-4 ring-white bg-gradient-to-br from-brand via-sun to-berry p-[3px] aspect-[16/7] md:aspect-[21/6]">
          {(() => {
            const b = banners[Math.min(heroIndex, Math.max(0, banners.length - 1))];
            const linked = !!(b?.category_id || String(b?.link_url || b?.href || '').trim());
            return (
              <button type="button" onClick={() => handleBannerClick(b)} className="absolute inset-[3px] rounded-xl md:rounded-[1.3rem] overflow-hidden">
                <img src={fixImageUrl(b.image_url) || b.image_url} alt={b.title || 'Offer'} loading="lazy" className="w-full h-full object-cover" />
                {b.title ? (
                  <span className="absolute left-0 right-0 bottom-0 bg-gradient-to-t from-ink/70 to-transparent text-white text-left px-4 py-3 font-extrabold text-sm">
                    {b.title}{linked ? ` · ${te ? 'తెరవడానికి నొక్కండి' : 'Tap to open'}` : ''}
                  </span>
                ) : null}
              </button>
            );
          })()}
          {banners.length > 1 ? (
            <div className="absolute bottom-2 right-3 flex gap-1.5">
              {banners.map((b, i) => (
                <button
                  key={`dot-${b.id}`}
                  type="button"
                  aria-label={`Slide ${i + 1}`}
                  onClick={() => setHeroIndex(i)}
                  className={`h-1.5 rounded-full transition-all ${i === heroIndex ? 'w-5 bg-white' : 'w-1.5 bg-white/50'}`}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {bannerPreview ? (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setBannerPreview(null)}
          className="fixed inset-0 z-[300] bg-ink/85 flex items-center justify-center p-5"
        >
          <div onClick={(e) => e.stopPropagation()} className="max-w-md w-full text-center">
            <img src={fixImageUrl(bannerPreview.image_url) || bannerPreview.image_url} alt={bannerPreview.title || 'Promotion'} className="w-full rounded-2xl" />
            {bannerPreview.title ? <p className="text-white font-bold mt-3">{bannerPreview.title}</p> : null}
            <Button variant="ghost" className="mt-4 !bg-white/15 !text-white" onClick={() => setBannerPreview(null)}>{te ? 'మూసివేయి' : 'Close'}</Button>
          </div>
        </div>
      ) : null}

      {offerFilterCat ? (
        <div className="flex items-center gap-2 mt-3">
          <span className="text-xs font-extrabold text-brand-dark">{te ? 'ఆఫర్ ఉత్పత్తులు' : 'Offer products'}</span>
          <button type="button" onClick={() => setOfferFilterCat(null)} className="text-xs font-extrabold text-red-600">✕ {te ? 'క్లియర్' : 'Clear'}</button>
        </div>
      ) : null}

      {/* search */}
      <div className="mt-3 md:hidden flex items-center gap-2 bg-white border border-black/5 rounded-full px-4 py-2.5 shadow-soft">
        <span className="text-ink-faint">🔍</span>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={te ? `${storeLabel}లో వెతకండి` : `Search in ${storeLabel}`}
          className="flex-1 min-w-0 outline-none text-sm bg-transparent"
        />
        {search ? <button type="button" onClick={() => setSearch('')} className="text-ink-faint">✕</button> : null}
      </div>

      {/* category chips */}
      <div ref={catsRef} id="categories-section" className="mt-3 space-y-2">
        {l1Cats.length > 0 && (
          <ChipRow>
            <Chip active={l1Id === 'all'} onClick={() => { setL1Id('all'); setL2Id('all'); setL3Id('all'); }}>{te ? 'అన్నీ' : t.allCategory}</Chip>
            {l1Cats.map((cat) => (
              <Chip key={cat.id} active={l1Id === cat.id} onClick={() => { setL1Id(cat.id); setL2Id('all'); setL3Id('all'); }}>
                {cat.emoji} {te ? cat.label_te : cat.label_en}
              </Chip>
            ))}
          </ChipRow>
        )}
        {l2Cats.length > 0 && (
          <ChipRow>
            <Chip active={l2Id === 'all'} onClick={() => { setL2Id('all'); setL3Id('all'); }}>{te ? 'అన్నీ' : 'All'}</Chip>
            {l2Cats.map((cat) => (
              <Chip key={cat.id} active={l2Id === cat.id} onClick={() => { setL2Id(cat.id); setL3Id('all'); }}>
                {cat.emoji} {te ? cat.label_te : cat.label_en}
              </Chip>
            ))}
          </ChipRow>
        )}
        {l3Cats.length > 0 && (
          <ChipRow>
            <Chip active={l3Id === 'all'} onClick={() => setL3Id('all')}>{te ? 'అన్నీ' : 'All'}</Chip>
            {l3Cats.map((cat) => (
              <Chip key={cat.id} active={l3Id === cat.id} onClick={() => setL3Id(cat.id)}>{cat.emoji} {te ? cat.label_te : cat.label_en}</Chip>
            ))}
          </ChipRow>
        )}
      </div>

      {/* product grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mt-4 pb-6">
        {loading ? (
          Array.from({ length: 10 }).map((_, i) => <SkeletonCard key={`skel-${i}`} />)
        ) : loadError ? (
          <div className="col-span-full">
            <EmptyState icon="⚙️" title={loadError} action={<Button onClick={retryLoad}>{te ? 'మళ్లీ ప్రయత్నించండి' : 'Retry'}</Button>} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="col-span-full">
            <EmptyState
              icon="📦"
              title={search ? `"${search}" — ${te ? 'ఫలితాలు లేవు' : 'no results'}` : (te ? `${storeLabel}లో ఉత్పత్తులు లేవు` : `No products in ${storeLabel} yet`)}
            />
          </div>
        ) : (
          filtered.map((p) => <ProductCard key={p.id} product={p} onOpenDetail={() => openProduct(p)} />)
        )}
      </div>

      {itemCount > 0 ? (
        <div className="md:hidden fixed bottom-16 inset-x-0 z-30 px-3">
          <button
            type="button"
            onClick={() => navigate('/cart')}
            className="w-full flex items-center justify-between bg-gradient-to-r from-brand-dark via-brand to-brand-glow text-white rounded-2xl px-5 py-3.5 shadow-lift"
          >
            <span className="text-left">
              <span className="block text-[11px] opacity-80">{itemCount} {te ? 'వస్తువులు' : `item${itemCount > 1 ? 's' : ''}`}</span>
              <span className="block font-black">₹{total.toFixed(2)}</span>
            </span>
            <span className="font-extrabold text-sm">{te ? 'కార్ట్ చూడండి' : 'View Cart'} →</span>
          </button>
        </div>
      ) : null}

      {placedResult ? (
        <div onClick={closePlaced} className="fixed inset-0 z-[400] bg-ink/50 flex items-center justify-center p-5">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl px-6 py-8 max-w-sm w-full text-center shadow-lift">
            <div className="text-5xl mb-2">{unpaidPlaced ? '⚠️' : '🎉'}</div>
            <div className={`font-black text-xl mb-2 ${unpaidPlaced ? 'text-gold' : 'text-brand-dark'}`}>
              {unpaidPlaced ? (te ? 'చెల్లింపు పూర్తికాలేదు' : 'Payment not completed') : (te ? 'ఆర్డర్ విజయవంతం!' : 'Order placed successfully')}
            </div>
            <div className="text-sm text-ink-soft leading-relaxed mb-4">
              {placedResult.order_number ? `#${placedResult.order_number}` : ''}
              {unpaidPlaced
                ? (te ? ' — ఆర్డర్ pending. Ordersలో Pay చేయండి లేదా COD ఎంచుకోండి.' : ' — Order is pending. Pay from Orders, or switch to Cash on Delivery.')
                : (te ? ' — మేము మీ ఆర్డర్ సిద్ధం చేస్తాం.' : ' — We will prepare your order shortly.')}
            </div>
            <Button variant="accent" fullWidth onClick={viewOrders}>{te ? 'ఆర్డర్లు చూడండి' : 'View my orders'}</Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ChipRow({ children }) {
  return <div className="flex gap-2 overflow-x-auto no-scrollbar">{children}</div>;
}

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${
        active ? 'bg-brand-dark text-white border-brand-dark' : 'bg-white text-ink-soft border-black/10'
      }`}
    >
      {children}
    </button>
  );
}
